// Gabarit PDF UNIQUE piloté par les tokens de mise en page (spec pdf-layouts §3).
// Les 6 styles seedés (et leurs variantes éditées par IA) passent tous par ici.
// Rendu par PDFViewer (préview) ET par buildDocumentPdfBlob (fichier final).
import { Document, Page, Text, View, Image, Font, StyleSheet, pdf } from '@react-pdf/renderer';
import interRegular from '../../assets/fonts/Inter-Regular.ttf';
import interBold from '../../assets/fonts/Inter-Bold.ttf';
import interItalic from '../../assets/fonts/Inter-Italic.ttf';
import playfairBold from '../../assets/fonts/PlayfairDisplay-Bold.ttf';
import type { PdfBlock, PdfSegment } from '../../services/documents/markdownDoc';
import type { PdfLayoutTokens } from '../../services/documents/pdfLayoutTokens';

Font.register({
  family: 'Inter',
  fonts: [
    { src: interRegular },
    { src: interBold, fontWeight: 700 },
    { src: interItalic, fontStyle: 'italic' },
  ],
});
Font.register({ family: 'Playfair', fonts: [{ src: playfairBold, fontWeight: 700 }] });

export interface DocumentPdfProps {
  firmName: string;
  logoUrl: string | null;
  colorPrimary: string;
  colorGold: string;
  title: string;
  recipientLines: string[];
  dateLine: string;
  blocks: PdfBlock[];
  signerName: string;
  layout: PdfLayoutTokens;
}

const INK = '#1a1a1a';
const MUTED = '#8a8a8a';
const RAIL_WIDTH = 30;

function Segments({ segments }: { segments: PdfSegment[] }) {
  return (
    <>
      {segments.map((s, i) => (
        <Text
          key={i}
          style={{
            ...(s.bold ? { fontWeight: 700 } : {}),
            ...(s.italic ? { fontStyle: 'italic' } : {}),
            ...(s.marker ? { backgroundColor: '#fde68a' } : {}),
          }}
        >
          {s.text}
        </Text>
      ))}
    </>
  );
}

export function DocumentPdfDoc(props: DocumentPdfProps) {
  const { firmName, logoUrl, colorPrimary, colorGold, title: _title, recipientLines, dateLine, blocks, signerName, layout } = props;
  const { header: h, title: t, body: b, footer: f, rail } = layout;

  const roleColor = (c: 'primary' | 'ink' | 'white') =>
    c === 'primary' ? colorPrimary : c === 'white' ? '#ffffff' : INK;
  const ruleColor = (r: 'gold' | 'dark' | 'light' | 'ink') =>
    r === 'gold' ? colorGold : r === 'light' ? '#cccccc' : INK;

  const firmStyle = {
    fontFamily: h.firmFont === 'heading' ? 'Playfair' : 'Inter',
    fontWeight: 700 as const,
    fontSize: h.firmSize,
    color: roleColor(h.firmColor),
    ...(h.firmUppercase ? { textTransform: 'uppercase' as const, letterSpacing: 1.2 } : {}),
  };
  const headingBase = {
    fontFamily: t.font === 'heading' ? 'Playfair' : 'Inter',
    fontWeight: 700 as const,
    color: t.color === 'primary' ? colorPrimary : INK,
    ...(t.uppercase ? { textTransform: 'uppercase' as const, letterSpacing: 0.8 } : {}),
    ...(t.align === 'center' ? { textAlign: 'center' as const } : {}),
    ...(t.underline !== 'none'
      ? { borderBottomWidth: 1.5, borderBottomColor: ruleColor(t.underline), paddingBottom: 2, alignSelf: t.align === 'center' ? ('center' as const) : ('flex-start' as const) }
      : {}),
    ...(t.accentBar ? { borderLeftWidth: 3, borderLeftColor: colorGold, paddingLeft: 8 } : {}),
  };

  const pagePadding = {
    paddingTop: h.variant === 'band' ? 0 : 56,
    paddingBottom: 64,
    paddingLeft: rail.enabled ? 60 + RAIL_WIDTH : 60,
    paddingRight: 60,
  };

  const styles = StyleSheet.create({
    page: { ...pagePadding, fontFamily: 'Inter', fontSize: b.fontSize, lineHeight: 1.5, color: INK },
    para: { marginBottom: 8, ...(b.justify ? { textAlign: 'justify' as const } : {}) },
    listRow: { flexDirection: 'row', marginBottom: 4, paddingLeft: 12 },
    bulletChar: { width: 14 },
    recipient: { alignSelf: 'flex-end', maxWidth: 220, marginBottom: 18 },
    dateLine: { alignSelf: 'flex-end', marginBottom: 24 },
    signature: { marginTop: 32, alignSelf: 'flex-end', textAlign: 'center' },
    footer: {
      position: 'absolute', bottom: 28, left: rail.enabled ? 60 + RAIL_WIDTH : 60, right: 60,
      flexDirection: 'row', fontSize: 8, color: MUTED,
      justifyContent: f.align === 'center' ? 'center' : 'space-between',
      ...(f.align === 'center' ? { gap: 14 } : {}),
      ...(f.rule !== 'none' ? { borderTopWidth: 1, borderTopColor: ruleColor(f.rule), paddingTop: 6 } : {}),
    },
    footBand: { position: 'absolute', bottom: 0, left: 0, right: 0, height: 10, backgroundColor: colorPrimary },
    railBar: {
      position: 'absolute', top: 0, bottom: 0, left: 0, width: RAIL_WIDTH,
      backgroundColor: colorPrimary, alignItems: 'center', paddingTop: 16,
    },
    railDot: {
      width: 18, height: 18, borderRadius: 9, backgroundColor: colorGold,
      alignItems: 'center', justifyContent: 'center',
    },
  });

  const headerRule = h.rule !== 'none'
    ? { borderBottomWidth: h.rule === 'gold' ? 2 : 1.5, borderBottomColor: ruleColor(h.rule), paddingBottom: 8 }
    : {};

  const renderHeader = () => {
    if (h.variant === 'band') {
      return (
        <View fixed style={{ backgroundColor: colorPrimary, marginBottom: 22, paddingVertical: 16, paddingHorizontal: 60, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 9 }}>
            {h.showLogo && logoUrl ? <Image src={logoUrl} style={{ height: 30, objectFit: 'contain' }} /> : null}
            <Text style={firmStyle}>{firmName}</Text>
          </View>
          {h.coordsText ? <Text style={{ fontSize: 7.5, color: '#ffffff', opacity: 0.75, textAlign: 'right' }}>{h.coordsText}</Text> : null}
        </View>
      );
    }
    if (h.variant === 'centered') {
      return (
        <View fixed style={{ alignItems: 'center', marginBottom: 20 }}>
          {h.showLogo && logoUrl ? <Image src={logoUrl} style={{ height: 34, objectFit: 'contain', marginBottom: 6 }} /> : null}
          <Text style={firmStyle}>{firmName}</Text>
          {h.subtitle ? <Text style={{ fontSize: 8, letterSpacing: 2.2, textTransform: 'uppercase', color: MUTED, marginTop: 2 }}>{h.subtitle}</Text> : null}
          {h.coordsText ? <Text style={{ fontSize: 7.5, color: MUTED, marginTop: 2 }}>{h.coordsText}</Text> : null}
          {h.rule !== 'none' ? <View style={{ width: 120, height: 2, backgroundColor: ruleColor(h.rule), marginTop: 8 }} /> : null}
        </View>
      );
    }
    if (h.variant === 'contemporary') {
      return (
        <View fixed style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 22, ...headerRule }}>
          <View style={{ flexDirection: 'row', alignItems: 'flex-end' }}>
            {h.showLogo && logoUrl ? <Image src={logoUrl} style={{ height: 28, objectFit: 'contain', marginRight: 8 }} /> : null}
            <Text style={firmStyle}>{firmName}</Text>
            <Text style={{ ...firmStyle, color: colorGold }}>.</Text>
          </View>
          {h.coordsText ? <Text style={{ fontSize: 7.5, color: MUTED, textAlign: 'right' }}>{h.coordsText}</Text> : null}
        </View>
      );
    }
    // 'left', 'letterhead', 'rail' : bloc aligné à gauche (letterhead = coords dessous + filet)
    return (
      <View fixed style={{ marginBottom: 20, ...headerRule }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 9 }}>
          {h.showLogo && logoUrl ? <Image src={logoUrl} style={{ height: 36, objectFit: 'contain' }} /> : null}
          <Text style={firmStyle}>{firmName}</Text>
        </View>
        {h.subtitle ? <Text style={{ fontSize: 8, color: MUTED, marginTop: 2 }}>{h.subtitle}</Text> : null}
        {h.coordsText ? <Text style={{ fontSize: 7.5, color: h.variant === 'letterhead' ? '#444444' : MUTED, marginTop: 3, lineHeight: 1.5 }}>{h.coordsText}</Text> : null}
      </View>
    );
  };

  let ordinal = 0;
  return (
    <Document title={props.title}>
      <Page size="A4" style={styles.page}>
        {rail.enabled ? (
          <View fixed style={styles.railBar}>
            <View style={styles.railDot}>
              <Text style={{ fontFamily: 'Playfair', fontWeight: 700, fontSize: 10, color: colorPrimary }}>
                {firmName.charAt(0)}
              </Text>
            </View>
            {rail.showFirmVertical ? (
              <Text style={{ fontSize: 7, color: '#ffffffd9', letterSpacing: 3, textTransform: 'uppercase', marginTop: 14, transform: 'rotate(90deg)', transformOrigin: 'left top', position: 'absolute', top: 52, left: 18, width: 300 }}>
                {firmName}
              </Text>
            ) : null}
          </View>
        ) : null}
        {renderHeader()}
        {recipientLines.length > 0 && (
          <View style={styles.recipient}>
            {recipientLines.map((l, i) => <Text key={i}>{l}</Text>)}
          </View>
        )}
        <Text style={styles.dateLine}>{dateLine}</Text>
        {blocks.map((blk, i) => {
          if (blk.kind === 'heading1') return <Text key={i} style={{ ...headingBase, fontSize: b.fontSize + 4.5, marginTop: 6, marginBottom: 12 }}><Segments segments={blk.segments} /></Text>;
          if (blk.kind === 'heading2') return <Text key={i} style={{ ...headingBase, fontSize: b.fontSize + 1.5, marginTop: 12, marginBottom: 6 }}><Segments segments={blk.segments} /></Text>;
          if (blk.kind === 'heading3') return <Text key={i} style={{ ...headingBase, fontSize: b.fontSize + 0.5, marginTop: 10, marginBottom: 4 }}><Segments segments={blk.segments} /></Text>;
          if (blk.kind === 'bullet' || blk.kind === 'ordered') {
            ordinal = blk.kind === 'ordered' ? ordinal + 1 : 0;
            return (
              <View key={i} style={styles.listRow}>
                <Text style={styles.bulletChar}>{blk.kind === 'bullet' ? '•' : `${ordinal}.`}</Text>
                <Text style={{ flex: 1 }}><Segments segments={blk.segments} /></Text>
              </View>
            );
          }
          return <Text key={i} style={styles.para}><Segments segments={blk.segments} /></Text>;
        })}
        <View style={styles.signature}>
          <Text style={{ fontWeight: 700 }}>{signerName}</Text>
          <Text>Avocat</Text>
        </View>
        <View style={styles.footer} fixed>
          <Text>{f.text ?? firmName}</Text>
          {f.showPagination ? <Text render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} /> : null}
        </View>
        {f.band ? <View fixed style={styles.footBand} /> : null}
      </Page>
    </Document>
  );
}

export async function buildDocumentPdfBlob(props: DocumentPdfProps): Promise<Blob> {
  return pdf(<DocumentPdfDoc {...props} />).toBlob();
}
